import { cn } from '@/util/cn';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

const alertVariants = cva('relative w-full rounded-lg [&_svg]:absolute', {
  variants: {
    variant: {
      solid: 'border',
      outlined: 'border',
      ghost: 'border-0',
      soft: 'border-1'
    },
    color: {
      default: '',
      destructive: '',
      secondary: '',
      info: '',
      success: '',
      warning: ''
    },
    size: {
      sm: 'px-3 py-2 text-xs [&_svg]:size-3 [&_svg]:left-3 [&_svg]:top-2 [&_svg~*]:pl-6 [&_svg+div]:translate-y-[-1px]',
      md: 'px-4 py-3 text-sm [&_svg]:size-4 [&_svg]:left-4 [&_svg]:top-3 [&_svg~*]:pl-6 [&_svg+div]:translate-y-[-2px]',
      lg: 'px-5 py-4 text-base [&_svg]:size-5 [&_svg]:left-5 [&_svg]:top-4 [&_svg~*]:pl-7 [&_svg+div]:translate-y-[-3px]',
      xl: 'px-6 py-5 text-lg [&_svg]:size-6 [&_svg]:left-6 [&_svg]:top-5 [&_svg~*]:pl-8 [&_svg+div]:translate-y-[-4px]'
    }
  },
  compoundVariants: [
    {
      color: 'default',
      variant: 'solid',
      className: ' bg-primary text-primary-foreground [&_svg]:text-primary-foreground'
    },
    {
      color: 'default',
      variant: 'outlined',
      className: 'border-primary/30 text-primary bg-white [&_svg]:text-primary'
    },
    {
      color: 'default',
      variant: 'ghost',
      className: 'text-primary [&_svg]:text-primary'
    },
    {
      color: 'default',
      variant: 'soft',
      className: 'bg-primary/10 text-primary [&_svg]:text-primary border-primary/30'
    },
    {
      color: 'destructive',
      variant: 'solid',
      className:
        'bg-destructive text-destructive-foreground [&_svg]:text-destructive-foreground'
    },
    {
      color: 'destructive',
      variant: 'outlined',
      className:
        'border-destructive/30 text-destructive bg-white [&_svg]:text-destructive'
    },
    {
      color: 'destructive',
      variant: 'ghost',
      className: 'text-destructive [&_svg]:text-destructive'
    },
    {
      color: 'destructive',
      variant: 'soft',
      className:
        'bg-destructive/10 text-destructive [&_svg]:text-destructive border-destructive/50'
    },
    {
      color: 'secondary',
      variant: 'solid',
      className:
        'bg-secondary text-secondary-foreground [&_svg]:text-secondary-foreground'
    },
    {
      color: 'secondary',
      variant: 'outlined',
      className: 'border-secondary/30 text-secondary bg-white [&_svg]:text-secondary'
    },
    {
      color: 'secondary',
      variant: 'ghost',
      className: 'text-secondary [&_svg]:text-secondary'
    },
    {
      color: 'secondary',
      variant: 'soft',
      className:
        'bg-secondary/10 text-secondary [&_svg]:text-secondary border-secondary/30'
    },
    {
      color: 'info',
      variant: 'solid',
      className: 'bg-blue-500 text-white [&_svg]:text-white'
    },
    {
      color: 'info',
      variant: 'outlined',
      className: 'border-blue-500/30 text-blue-500 bg-white [&_svg]:text-blue-500'
    },
    {
      color: 'info',
      variant: 'ghost',
      className: 'text-blue-500 [&_svg]:text-blue-500'
    },
    {
      color: 'info',
      variant: 'soft',
      className: 'bg-blue-50 text-blue-500 [&_svg]:text-blue-500 border-info/30'
    },
    {
      color: 'success',
      variant: 'solid',
      className: 'bg-success text-success-foreground [&_svg]:text-white'
    },
    {
      color: 'success',
      variant: 'outlined',
      className: 'border-success/30 text-success bg-white [&_svg]:text-success'
    },
    {
      color: 'success',
      variant: 'ghost',
      className: 'text-success [&_svg]:text-success'
    },
    {
      color: 'success',
      variant: 'soft',
      className: ' bg-success/10 text-success [&_svg]:text-success border-success/30'
    },
    {
      color: 'warning',
      variant: 'solid',
      className: 'bg-warning text-white [&_svg]:text-white'
    },
    {
      color: 'warning',
      variant: 'outlined',
      className: 'border-warning/30 text-warning bg-white [&_svg]:text-warning'
    },
    {
      color: 'warning',
      variant: 'ghost',
      className: 'text-warning [&_svg]:text-warning'
    },
    {
      color: 'warning',
      variant: 'soft',
      className: 'bg-warning/10 text-warning [&_svg]:text-warning border-warning/30'
    }
  ],
  defaultVariants: {
    color: 'secondary',
    size: 'md',
    variant: 'outlined'
  }
});

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, color, size, variant, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({ color, size, variant }), className)}
    {...props}
  />
));
Alert.displayName = 'Alert';

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn('mb-1 leading-none font-bold tracking-tight', className)}
    {...props}
  />
));
AlertTitle.displayName = 'AlertTitle';

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('text-pretty [&_p]:leading-relaxed', className)}
    {...props}
  />
));
AlertDescription.displayName = 'AlertDescription';

export { Alert, AlertTitle, AlertDescription };
